import { Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import type { SectionChange } from './documentSections';

const VERB: Record<SectionChange['kind'], string> = {
  added: 'added',
  changed: 'changed',
  removed: 'removed',
};

/**
 * v3 conversational layout — sits above the note after an AI turn revises
 * the document, stating what changed rather than letting the note change
 * silently (docs/design/consultation/Conversation.dc.html marker 2). Keep
 * just dismisses the ribbon (the edit is already live in the document);
 * Undo reverts to the snapshot taken right before this turn — see
 * ConsultationChat.tsx's own comment on why this is single-level.
 */
export function ChangeRibbon({ changes, onJump, onKeep, onUndo, className }: {
  changes: SectionChange[];
  onJump: (key: string) => void;
  onKeep: () => void;
  onUndo: () => void;
  className?: string;
}) {
  if (!changes.length) return null;
  const [first] = changes;

  return (
    <div className={cn('flex flex-wrap items-center gap-2 border-b border-line bg-accent/40 px-3 py-1.5 text-[12px]', className)}>
      {changes.length === 1 && first ? (
        <span className="text-ink">
          <b>1 change</b> from your last message — “{first.heading}” {VERB[first.kind]}
        </span>
      ) : (
        <>
          <span className="text-ink"><b>{changes.length} changes</b> from your last message</span>
          <div className="flex flex-wrap gap-1">
            {changes.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => onJump(c.key)}
                className="rounded border border-border px-1.5 py-0.5 text-[11px] text-ink-muted hover:border-teal hover:text-teal"
              >
                ↧ {c.heading}
              </button>
            ))}
          </div>
        </>
      )}
      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        <Button type="button" size="sm" variant="ghost" onClick={onKeep}>Keep</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onUndo}>
          <Undo2 className="h-3.5 w-3.5" aria-hidden="true" /> Undo
        </Button>
      </div>
    </div>
  );
}
